import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { portfolioPhotos, portfolioVideos } from '../data/mock';

const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState('prewedding');
  const [photoCount, setPhotoCount] = useState(6);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Fade animation state
  const [isFading, setIsFading] = useState(false);

  const filteredPhotos = portfolioPhotos
    .filter((photo) => photo.category === activeFilter)
    .slice(0, photoCount);

  const allFilteredPhotos = portfolioPhotos.filter(
    (photo) => photo.category === activeFilter
  );

  const loadMorePhotos = () => {
    setPhotoCount((prev) => prev + 6);
  };

  const triggerFade = (callback) => {
    setIsFading(true);
    setTimeout(() => {
      callback();
      setTimeout(() => setIsFading(false), 50);
    }, 200); // fade-out duration
  };

  const nextVideo = () => {
    triggerFade(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % portfolioVideos.length);
    });
  };

  const prevVideo = () => {
    triggerFade(() => {
      setCurrentVideoIndex((prev) =>
        (prev - 1 + portfolioVideos.length) % portfolioVideos.length
      );
    });
  };

  useEffect(() => {
    const interval = setInterval(nextVideo, 5000);
    return () => clearInterval(interval);
  }, []);

  const openPhotoModal = (photo) => setSelectedPhoto(photo);
  const closePhotoModal = () => setSelectedPhoto(null);

  const navigatePhoto = (direction) => {
    const currentIndex = allFilteredPhotos.findIndex(
      (p) => p.id === selectedPhoto.id
    );
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % allFilteredPhotos.length;
    } else {
      newIndex =
        (currentIndex - 1 + allFilteredPhotos.length) %
        allFilteredPhotos.length;
    }
    setSelectedPhoto(allFilteredPhotos[newIndex]);
  };

  const openVideoModal = (video) => setSelectedVideo(video);
  const closeVideoModal = () => setSelectedVideo(null);

  return (
    <section id="portofolio" className="py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-fraunces font-bold text-[#00382F] mb-4">
            Portofolio Kami
          </h2>
          <p className="text-lg text-[#658C8D] font-lato max-w-2xl mx-auto">
            Lihat hasil karya terbaik kami yang telah mengabadikan momen berharga
          </p>
        </div>

        {/* Portfolio Videos Slider */}
        <div className="mb-16">
          <h3 className="text-3xl font-fraunces font-bold text-[#00382F] mb-8 text-center">
            Portofolio Video
          </h3>

          <div className="relative max-w-4xl mx-auto">
            {/* Video Thumbnail */}
            <div
              className={`
                group relative aspect-video overflow-hidden rounded-lg shadow-lg 
                hover:shadow-2xl transition-all duration-500 cursor-pointer
                ${isFading ? 'opacity-0' : 'opacity-100'} 
                transition-opacity duration-200
              `}
              onClick={() =>
                openVideoModal(portfolioVideos[currentVideoIndex])
              }
            >
              <img
                src={portfolioVideos[currentVideoIndex].thumbnail}
                alt={portfolioVideos[currentVideoIndex].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-lato font-semibold text-xl">
                  {portfolioVideos[currentVideoIndex].title}
                </p>
              </div>
            </div>

            {/* Navigation + Dots */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={prevVideo}
                className="text-[#658C8D] hover:text-[#00382F] transition-all duration-300"
              >
                <ChevronLeft size={26} />
              </button>

              <div className="flex gap-2">
                {portfolioVideos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() =>
                      triggerFade(() => setCurrentVideoIndex(index))
                    }
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentVideoIndex
                        ? 'w-8 bg-[#658C8D]'
                        : 'w-2 bg-[#C1CCBC] hover:bg-[#658C8D]'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextVideo}
                className="text-[#658C8D] hover:text-[#00382F] transition-all duration-300"
              >
                <ChevronRight size={26} />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex justify-center gap-4 mb-12">
          <Button
            onClick={() => {
              setActiveFilter('prewedding');
              setPhotoCount(6);
            }}
            className={`px-8 py-3 rounded-full font-lato font-semibold transition-all duration-300 ${
              activeFilter === 'prewedding'
                ? 'bg-[#658C8D] text-white shadow-lg scale-105'
                : 'bg-white text-[#00382F] border-2 border-[#658C8D] hover:bg-[#658C8D] hover:text-white'
            }`}
          >
            Prewedding
          </Button>
          <Button
            onClick={() => {
              setActiveFilter('wedding');
              setPhotoCount(6);
            }}
            className={`px-8 py-3 rounded-full font-lato font-semibold transition-all duration-300 ${
              activeFilter === 'wedding'
                ? 'bg-[#658C8D] text-white shadow-lg scale-105'
                : 'bg-white text-[#00382F] border-2 border-[#658C8D] hover:bg-[#658C8D] hover:text-white'
            }`}
          >
            Wedding
          </Button>
        </div>

        {/* Portfolio Photos */}
        <div className="mb-16">
          <h3 className="text-3xl font-fraunces font-bold text-[#00382F] mb-8 text-center">
            Portofolio Foto
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.id}
                className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer animate-fadeZoom"
                onClick={() => openPhotoModal(photo)}
              >
                <img
                  src={photo.imageUrl}
                  alt={photo.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <p className="text-white font-lato font-semibold">
                    {photo.alt}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredPhotos.length < allFilteredPhotos.length && (
            <div className="text-center mt-8">
              <Button
                onClick={loadMorePhotos}
                className="bg-[#658C8D] text-white hover:bg-[#00382F] px-8 py-3 rounded-full font-lato font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Lebih Banyak
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={closePhotoModal}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#C1CCBC] transition-colors duration-200"
            onClick={closePhotoModal}
          >
            <X size={40} />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C1CCBC] transition-colors duration-200 p-2 rounded-full bg-white/10 hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              navigatePhoto('prev');
            }}
          >
            <ChevronLeft size={40} />
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C1CCBC] transition-colors duration-200 p-2 rounded-full bg-white/10 hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              navigatePhoto('next');
            }}
          >
            <ChevronRight size={40} />
          </button>
          <img
            src={selectedPhoto.imageUrl}
            alt={selectedPhoto.alt}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={closeVideoModal}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#C1CCBC] transition-colors duration-200 z-10"
            onClick={closeVideoModal}
          >
            <X size={40} />
          </button>
          <div
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}`}
              title={selectedVideo.title}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;
